/**
 * WorkspacePage — canvas-first workspace, fully native.
 *
 * Layout matches v1: ReactFlow canvas dominant on the left, Director
 * chat as a slim sidebar on the right. Both panels use v1's vendored
 * hooks + components via the @console alias so behavior is identical
 * to v1; only the chrome (top bar, chat bubbles, status pills) is v2.
 *
 * Vendored v1 nodes (canvas-v2/nodes/*) call v1's `useAuth()` (via
 * `useAuthenticatedMediaUrl`) only to subscribe to token rotation —
 * no fields are read. We provide a static shim value through v1's
 * AuthContext (exported additively as `_AuthContextForVendoring`) so
 * those calls don't crash. We do NOT mount v1's full `AuthProvider`
 * because its mount-time + accessToken-change effects overwrite the
 * api-client's `setAuthTokenGetter` with a Firebase-ID-token getter,
 * which on localhost forces the backend into an ADC-verify retry loop
 * (3-9s per WS auth) and drops requests into the firebase-uid data
 * path instead of `debug-user` — manifesting as "stuck thinking".
 *
 * One Y.Doc per project — hoisted here so Canvas and ChatPanel share
 * the SAME WebSocket peer.
 *
 * v1 stays behaviorally untouched (only an additive export added).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import * as Y from 'yjs'
import { _AuthContextForVendoring as V1AuthContext } from '@console/contexts/AuthContext'
import { ProjectIdProvider } from '@console/contexts/ProjectIdContext'
import { useYDoc } from '@console/hooks/useYDoc'
import { api } from '@ponpon/api-client'
import { useAuth } from '../contexts/AuthContext'
import { ChatPanel, type ChatAttachment } from '../components/workspace/ChatPanel'
import { Canvas } from '../components/workspace/Canvas'
import {
  placeComposerUploads,
  rejectionReason,
  uploadKind,
  uploadOutputField,
  type ComposerUpload,
} from '../lib/uploads'

const DEFAULT_CANVAS_ID = 'agent'

export function WorkspacePage() {
  const { id } = useParams<{ id: string }>()
  const { session, loading } = useAuth()
  const location = useLocation()
  const navState = location.state as
    | { initialPrompt?: string; uploadedRefs?: ComposerUpload[] }
    | null
  const initialPrompt = navState?.initialPrompt
  const composerUploads = navState?.uploadedRefs

  // Single shared Y.Doc. Both Canvas (ReactFlow renderer) and ChatPanel
  // (for execution-plan cost auto-confirm) need this — hoisting here
  // avoids spinning up two parallel WebSocket peers per project.
  const { ydoc, synced, connected } = useYDoc({
    projectId: id ?? '',
    canvasId: DEFAULT_CANVAS_ID,
    enabled: !!id,
  })

  // Minimal stable shim for v1's AuthContext shape. Vendored v1 code
  // only calls `useAuth()` for re-render subscription (see
  // useAuthenticatedMediaUrl); no fields are read. Memoized so the
  // value identity is stable across renders — otherwise every parent
  // re-render would notify all v1 consumers.
  const v1AuthValue = useMemo(
    () => ({
      session: session
        ? {
            access_token: '',
            user: {
              id: session.user.id,
              email: session.user.email ?? undefined,
              app_metadata: {},
              user_metadata: {},
              identities: [],
              aud: 'authenticated',
              created_at: new Date(0).toISOString(),
            },
          }
        : null,
      user: session
        ? {
            id: session.user.id,
            email: session.user.email ?? undefined,
            app_metadata: {},
            user_metadata: {},
            identities: [],
            aud: 'authenticated',
            created_at: new Date(0).toISOString(),
          }
        : null,
      isLoading: false,
      isAuthenticated: !!session,
      signInWithGoogle: async () => {},
      signInWithMagicLink: async () => ({ error: null }),
      signOut: async () => {},
      hasPasswordIdentity: false,
      isSoftDeleted: false,
      softDeleteStatus: null,
    }),
    [session],
  )

  // Chat-side file attachment. Creates an `image` upload node directly in
  // YDoc so the Director sees it as a real canvas node (via
  // `referenced_node_ids` on the next message), then POSTs the file and
  // flips the node's status + writes `out_image` on success. Returns
  // {nodeId, name} so the ChatPanel can render a chip and pass the id
  // through `startWithPrompt(prompt, referencedNodeIds=[id])`.
  //
  // This is the v2 equivalent of v1's `handleUploadFromDirector` in
  // WorkspacePageV2 (which does the same thing via setNodes + addNodeToYDoc),
  // but minimal — no viewport math, no pan animation. The node lands at
  // a fixed canvas position because the user isn't dragging it onto a
  // specific spot; they're attaching it to chat.
  // Track in-flight chat-attached uploads so we can clean up the
  // optimistic YDoc nodes on unmount (page nav while a multi-MB photo
  // is still uploading would otherwise strand a `status: "running"`
  // image node in YDoc forever, visible as a permanent spinner).
  const inFlightUploadsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const inFlight = inFlightUploadsRef.current
    return () => {
      if (!ydoc || inFlight.size === 0) return
      const yNodes = ydoc.getMap<Y.Map<unknown>>('nodes')
      ydoc.transact(() => {
        for (const orphanId of inFlight) {
          if (yNodes.has(orphanId)) yNodes.delete(orphanId)
        }
      }, 'frontend')
      inFlight.clear()
    }
  }, [ydoc])

  // The Composer's attachments become canvas nodes before the initial prompt
  // is sent, so the Director receives them as referenced nodes — a "use my
  // photo" recipe is meaningless otherwise. With nothing attached, the prompt
  // goes out immediately as before; with attachments it waits for the YDoc
  // to sync so the idempotency check sees the real doc.
  const [composerRefIds, setComposerRefIds] = useState<string[] | null>(
    composerUploads?.length ? null : [],
  )
  useEffect(() => {
    if (composerRefIds !== null || !ydoc || !synced || !composerUploads?.length) return
    setComposerRefIds(placeComposerUploads(ydoc, composerUploads))
  }, [composerRefIds, ydoc, synced, composerUploads])

  const projectId = id
  const handleAttachToChat = useCallback(
    async (file: File): Promise<ChatAttachment> => {
      if (!ydoc || !projectId) {
        throw new Error('Workspace not ready for upload')
      }

      // Reject before the request goes out. Cloud Run caps a request body at
      // 32 MiB and answers larger ones with a plain-HTML 413 from the Google
      // frontend — the app never sees it. Measured against production: a
      // 40 MB .MOV made the browser retry the POST four times over 160
      // seconds while the node sat on "running" and the UI said nothing.
      // Most phone video is over the cap, so this is the common case.
      const rejection = rejectionReason(file)
      if (rejection) throw new Error(rejection)

      // The kind decides the node type. This used to be hardcoded to
      // 'image', so a video uploaded into the workspace arrived as an
      // "Upload Image" node holding a .MOV — visibly wrong, and unwireable
      // into a video editor, which is the entire point of uploading footage.
      const kind = uploadKind(file)
      const nodeId = `${kind}_upload_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`
      const yNodes = ydoc.getMap<Y.Map<unknown>>('nodes')

      // Optimistic insert — lands on canvas immediately so the user sees
      // "their portrait" while the bytes upload. status='running' shows
      // the spinner skin on the upload node.
      ydoc.transact(() => {
        const ynode = new Y.Map<unknown>()
        ynode.set('type', kind)
        ynode.set('label', file.name)
        ynode.set('status', 'running')
        ynode.set('x', 80)
        ynode.set('y', 80)
        ynode.set('tainted', true)
        ynode.set('in_file_name', file.name)
        yNodes.set(nodeId, ynode)
      }, 'frontend')
      inFlightUploadsRef.current.add(nodeId)

      try {
        const result = await api.uploadAsset(projectId, file)
        ydoc.transact(() => {
          const ynode = yNodes.get(nodeId)
          if (ynode) {
            ynode.set('status', 'completed')
            ynode.set(uploadOutputField(kind), result.path)
          }
        }, 'frontend')
        inFlightUploadsRef.current.delete(nodeId)
        return { nodeId, name: file.name, status: 'done' }
      } catch (err) {
        ydoc.transact(() => {
          const ynode = yNodes.get(nodeId)
          if (ynode) {
            ynode.set('status', 'error')
            ynode.set('error', err instanceof Error ? err.message : String(err))
          }
        }, 'frontend')
        inFlightUploadsRef.current.delete(nodeId)
        throw err
      }
    },
    [ydoc, projectId],
  )

  if (loading) return <div className="p-12 text-text-3 text-[0.92rem]">Loading…</div>
  if (!session) return <Navigate to="/login" replace />
  if (!id) return <Navigate to="/dashboard" replace />

  return (
    <V1AuthContext.Provider value={v1AuthValue}>
      <ProjectIdProvider value={id}>
        <div
          className="grid h-[calc(100vh-64px)] overflow-hidden bg-bg"
          style={{
            gridTemplateColumns: '1fr minmax(360px, 26%)',
            gridTemplateRows: 'minmax(0, 1fr)',
          }}
        >
          <div className="relative min-w-0 min-h-0 border-r border-line">
            <Canvas projectId={id} ydoc={ydoc} synced={synced} connected={connected} />
          </div>
          <div className="min-w-0 min-h-0">
            <ChatPanel
              projectId={id}
              ydoc={ydoc}
              initialPrompt={composerRefIds === null ? undefined : initialPrompt}
              initialReferencedNodeIds={composerRefIds ?? undefined}
              onAttachFile={handleAttachToChat}
            />
          </div>
        </div>
      </ProjectIdProvider>
    </V1AuthContext.Provider>
  )
}
